import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { BarChart3 } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';

const ResultsTable = ({ projections, allocations, totalCapital, aiSplit, monthlyContribution = 0 }) => {
  const { t } = useLanguage();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (percent) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Updated for 3 baskets
  const basketNames = {
    dcaCrypto: t('dcaCrypto'),
    aiTrading: t('aiTrading'),
    web3: t('web3')
  };

  const timeframes = [
    { key: 365, label: t('horizon1y') },
    { key: 1095, label: t('horizon3y') },
    { key: 1825, label: t('horizon5y') }
  ];

  // 3 baskets structure
  const baskets = ['dcaCrypto', 'aiTrading', 'web3'];
  
  // Handle backward compatibility
  const getAllocation = (basket) => {
    if (basket === 'dcaCrypto') {
      return allocations.dcaCrypto !== undefined 
        ? allocations.dcaCrypto 
        : ((allocations.bitcoin || 0) + (allocations.diversified || 0));
    }
    return allocations[basket] || 0;
  };
  
  const getProjectionValue = (projection, basket) => {
    if (!projection) return 0;
    if (basket === 'dcaCrypto') {
      return projection.dcaCrypto || projection.bitcoin || 0;
    }
    return projection[basket] || 0;
  };

  return (
    <Card className="bg-[#0b1c38] border-[#12b7ff] shadow-xl">
      <CardHeader>
        <CardTitle className="text-[#00f4ff] flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          {t('detailedProjections')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#0677aa]">
                <th className="text-left py-3 px-4 text-white font-semibold">{t('basket')}</th>
                <th className="text-right py-3 px-4 text-white font-semibold">{t('allocationUsd')}</th>
                {monthlyContribution > 0 && (
                  <th className="text-right py-3 px-4 text-white font-semibold">{t('basketContrib')}</th>
                )}
                {timeframes.map(timeframe => (
                  <th key={timeframe.key} className="text-right py-3 px-4 text-white font-semibold">
                    {timeframe.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {baskets.map(basket => {
                const allocationPercent = getAllocation(basket);
                const allocation = totalCapital * (allocationPercent / 100);
                return (
                  <tr key={basket} className="border-b border-[#0677aa]/30 hover:bg-[#040816]/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">
                          {basketNames[basket]}
                        </span>
                        <Badge variant="outline" className="border-[#0677aa] text-[#0677aa] text-xs">
                          {allocationPercent.toFixed(1)}%
                        </Badge>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="text-[#00f4ff] font-medium">
                        {formatCurrency(allocation)}
                      </div>
                    </td>
                    {monthlyContribution > 0 && (
                      <td className="py-3 px-4 text-right">
                        <div className="text-[#12b7ff] text-sm space-y-1">
                          <div>{formatCurrency((monthlyContribution * (allocationPercent / 100)) * 12)}</div>
                          <div>{formatCurrency((monthlyContribution * (allocationPercent / 100)) * 36)}</div>
                          <div>{formatCurrency((monthlyContribution * (allocationPercent / 100)) * 60)}</div>
                        </div>
                      </td>
                    )}
                    {timeframes.map(timeframe => {
                      const projection = projections[timeframe.key];
                      const futureValue = getProjectionValue(projection, basket) || allocation;
                      const gain = futureValue - allocation;
                      const gainPercent = allocation > 0 ? ((gain / allocation) * 100) : 0;
                      
                      return (
                        <td key={timeframe.key} className="py-3 px-4 text-right">
                          <div className="text-[#00f4ff] font-bold">
                            {formatCurrency(futureValue)}
                          </div>
                          <div className={`text-sm ${gain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatPercent(gainPercent)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              
              {/* AI Split Breakdown */}
              {getAllocation('aiTrading') > 0 && (
                <>
                  <tr>
                    <td colSpan={5} className="py-2">
                      <div className="text-[#0677aa] text-sm font-semibold ml-8">
                        {t('aiTradingBreakdown')}:
                      </div>
                    </td>
                  </tr>
                  {/* Order: Sigmai, Sugair, Jairvis (grayed) */}
                  {[
                    { key: 'sigmai', value: aiSplit.sigmai || 70, disabled: false },
                    { key: 'sugair', value: aiSplit.sugair || 30, disabled: false },
                    { key: 'jairvis', value: aiSplit.jairvis !== undefined ? aiSplit.jairvis : (aiSplit.jarvis || 0), disabled: true }
                  ].map(({ key: bot, value: percentage, disabled }) => {
                    const aiAllocation = totalCapital * (getAllocation('aiTrading') / 100);
                    const botAllocation = aiAllocation * (percentage / 100);
                    
                    return (
                      <tr key={bot} className={`border-b border-[#0677aa]/20 ${disabled ? 'opacity-50' : ''}`}>
                        <td className="py-2 px-4 pl-12">
                          <div className="flex items-center gap-2">
                            <span className={`capitalize ${disabled ? 'text-gray-500' : 'text-gray-300'}`}>
                              {bot === 'jairvis' ? 'Jairvis' : bot.charAt(0).toUpperCase() + bot.slice(1)}
                              {disabled && <span className="ml-1 text-yellow-500">🔧</span>}
                            </span>
                            <Badge variant="outline" className={`text-xs ${disabled ? 'border-gray-600 text-gray-500' : 'border-[#0677aa]/50 text-[#0677aa]/70'}`}>
                              {percentage.toFixed(0)}% {t('ofAI')}
                            </Badge>
                          </div>
                        </td>
                        <td className="py-2 px-4 text-right">
                          <div className={`text-sm ${disabled ? 'text-gray-500' : 'text-[#12b7ff]'}`}>
                            {formatCurrency(botAllocation)}
                          </div>
                        </td>
                        {monthlyContribution > 0 && <td></td>}
                        {timeframes.map(timeframe => {
                          const projection = projections[timeframe.key];
                          const aiProjection = projection ? projection.aiSplit : {};
                          const botFutureValue = aiProjection[bot] || botAllocation;
                          const botGain = botFutureValue - botAllocation;
                          const botGainPercent = botAllocation > 0 ? ((botGain / botAllocation) * 100) : 0;
                          
                          return (
                            <td key={timeframe.key} className="py-2 px-4 text-right">
                              <div className="text-[#12b7ff] text-sm font-medium">
                                {formatCurrency(botFutureValue)}
                              </div>
                              <div className={`text-xs ${botGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {formatPercent(botGainPercent)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </>
              )}
              
              {/* Total Row */}
              <tr className="border-t-2 border-[#00f4ff] bg-[#040816]/30">
                <td className="py-4 px-4">
                  <div className="text-[#00f4ff] font-bold text-lg">
                    {t('totalPortfolio')}
                  </div>
                </td>
                <td className="py-4 px-4 text-right">
                  <div className="text-[#00f4ff] font-bold text-lg">
                    {formatCurrency(totalCapital)}
                  </div>
                </td>
                {monthlyContribution > 0 && (
                  <td className="py-4 px-4 text-right">
                    <div className="text-[#12b7ff] font-bold space-y-1">
                      <div>{formatCurrency(monthlyContribution * 12)}</div>
                      <div>{formatCurrency(monthlyContribution * 36)}</div>
                      <div>{formatCurrency(monthlyContribution * 60)}</div>
                    </div>
                  </td>
                )}
                {timeframes.map(timeframe => {
                  const projection = projections[timeframe.key];
                  const totalValue = projection ? projection.total : totalCapital;
                  const totalInvested = projection ? projection.totalInvested : totalCapital;
                  const netGain = projection ? projection.netGain : 0;
                  const roi = projection ? projection.roi : 0;
                  
                  return (
                    <td key={timeframe.key} className="py-4 px-4 text-right">
                      <div className="text-[#00f4ff] font-bold text-lg">
                        {formatCurrency(totalValue)}
                      </div>
                      <div className={`font-semibold ${netGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatPercent(roi)}
                      </div>
                      <div className={`text-sm ${netGain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(netGain)}
                      </div>
                    </td>
                  );
                })}
              </tr>
              
              {/* Summary Metrics */}
              {monthlyContribution > 0 && (
                <>
                  <tr className="border-t border-[#0677aa]/50">
                    <td colSpan={monthlyContribution > 0 ? 6 : 5} className="py-3 px-4">
                      <div className="grid grid-cols-3 gap-8 text-center">
                        <div>
                          <div className="text-[#0677aa] text-sm">{t('contributionsTotal')}</div>
                          <div className="text-[#12b7ff] font-semibold space-x-4">
                            <span>{formatCurrency(monthlyContribution * 12)}</span>
                            <span>{formatCurrency(monthlyContribution * 36)}</span>
                            <span>{formatCurrency(monthlyContribution * 60)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-[#0677aa] text-sm">Total Invested</div>
                          <div className="text-[#00f4ff] font-semibold space-x-4">
                            <span>{formatCurrency(totalCapital + monthlyContribution * 12)}</span>
                            <span>{formatCurrency(totalCapital + monthlyContribution * 36)}</span>
                            <span>{formatCurrency(totalCapital + monthlyContribution * 60)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-[#0677aa] text-sm">{t('netGain')} & {t('roi')}</div>
                          <div className="text-green-400 font-semibold space-x-4">
                            {timeframes.map(timeframe => {
                              const projection = projections[timeframe.key];
                              const netGain = projection ? projection.netGain : 0;
                              const roi = projection ? projection.roi : 0;
                              return (
                                <span key={timeframe.key}>
                                  {formatCurrency(netGain)} ({formatPercent(roi)})
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultsTable;